import {
  HStack,
  Box,
  Spinner,
  ModalFooter,
  SimpleGrid,
  Wrap,
  WrapItem,
  VStack,
  Icon,
  IconButton,
} from "@chakra-ui/react";
import {
  FiCpu,
  FiImage,
  FiMessageSquare,
  FiVideo,
  FiEye,
  FiX,
} from "react-icons/fi";
import { RiRobotLine } from "react-icons/ri";
import { useState, useMemo, useEffect } from "react";
import { motion } from "framer-motion";

import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
} from "@chakra-ui/react";
import { AIModel } from "types";
import { Text, Button } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { ModelInfoCard } from "./ModelInfoCard";
import { MdDataArray } from "react-icons/md";
import {
  FaBrain,
  FaDatabase,
  FaEye,
  FaMicrophone,
  FaMusic,
  FaSort,
  FaSortAlphaUp,
  FaSortNumericDown,
  FaSortNumericUp,
} from "react-icons/fa";

interface ModelInfoModalProps {
  isOpen: boolean;
  onClose: () => void;
  models: AIModel[];
  isLoading: boolean;
  isModelInfoOpen: boolean;
  onModelInfoClose: () => void;
  defaultProvider?: string;
  currencyPairs: any;
}

type SortDirection = "none" | "asc" | "desc";

export const ModelInfoModal = ({
  isOpen,
  onClose,
  models,
  isLoading,
  isModelInfoOpen,
  onModelInfoClose,
  defaultProvider,
  currencyPairs,
}: ModelInfoModalProps) => {
  const { t } = useTranslation();
  const [selectedProvider, setSelectedProvider] = useState<string | null>(
    defaultProvider || null
  );
  const [selectedCapability, setSelectedCapability] = useState<string | null>(
    null
  );
  const [showDisabled, setShowDisabled] = useState(false);
  const [costSort, setCostSort] = useState<SortDirection>("none");

  // Get unique providers and capabilities
  const providers = useMemo(
    () => Array.from(new Set(models.map((model) => model.provider))).sort(),
    [models]
  );

  const capabilities = useMemo(
    () =>
      Array.from(
        new Set(models.flatMap((model) => model.capabilities || []))
      ).sort(),
    [models]
  );

  // Helper function to get model cost
  const getModelCost = (model: AIModel) => {
    if (model.type === "llm") {
      return model.outputCostPer1M || 0;
    }
    return model.costPerGeneration || 0;
  };

  // Filter and sort models based on selected filters
  const filteredModels = useMemo(() => {
    let filtered = models.filter((model) => {
      const providerMatch =
        !selectedProvider || model.provider === selectedProvider;
      const capabilityMatch =
        !selectedCapability || model.capabilities?.includes(selectedCapability);
      const enabledMatch = showDisabled || model.enabled;
      return providerMatch && capabilityMatch && enabledMatch;
    });

    if (costSort !== "none") {
      filtered.sort((a, b) => {
        const costA = getModelCost(a);
        const costB = getModelCost(b);

        // Handle special cases where both costs are 0
        if (costA === 0 && costB === 0) return 0;
        // If only one cost is 0, treat it as the lowest possible value
        if (costA === 0) return costSort === "asc" ? -1 : 1;
        if (costB === 0) return costSort === "asc" ? 1 : -1;

        // For non-zero values, multiply by 1000000 to avoid floating point issues
        const scaledA = Math.round(costA * 1000000);
        const scaledB = Math.round(costB * 1000000);
        return costSort === "asc" ? scaledA - scaledB : scaledB - scaledA;
      });
    }

    return filtered;
  }, [models, selectedProvider, selectedCapability, showDisabled, costSort]);

  const getModelIcon = (capability: string) => {
    if (capability === "image") {
      return FiImage;
    }
    if (capability === "video") {
      return FiVideo;
    }
    if (capability === "chat") {
      return FiMessageSquare;
    }
    if (capability === "multimodal") {
      return RiRobotLine;
    }
    if (capability === "embedding") {
      return MdDataArray;
    }
    if (capability === "reasoning") {
      return FaBrain;
    }
    if (capability === "vision") {
      return FaEye;
    }
    if (capability === "text-to-speech") {
      return FaMicrophone;
    }
    if (capability === "reranking") {
      return FaSortNumericUp;
    }
    if (capability === "music") {
      return FaMusic;
    }
    if (capability === "data-mining") {
      return FaDatabase;
    }
    return FiCpu;
  };
  const getModelCapabilityName = (capability: string) => {
    if (capability === "reasoning") {
      return t("chat.modelInfo.reasoning");
    }
    if (capability === "vision") {
      return t("chat.modelInfo.vision");
    }
    if (capability === "tool-calling") {
      return t("chat.modelInfo.toolCalling");
    }
    if (capability === "embedding") {
      return t("chat.modelInfo.embedding");
    }
    if (capability === "image") {
      return t("chat.modelInfo.image");
    }
    if (capability === "video") {
      return t("chat.modelInfo.video");
    }
    if (capability === "chat") {
      return t("chat.modelInfo.chat");
    }
    if (capability === "multimodal") {
      return t("chat.modelInfo.multimodal");
    }
    if (capability === "text-to-speech") {
      return t("chat.modelInfo.textToSpeech");
    }
    if (capability === "reranking") {
      return t("chat.modelInfo.reranking");
    }
    if (capability === "music") {
      return t("chat.modelInfo.music");
    }
    if (capability === "data-mining") {
      return t("chat.modelInfo.dataMining");
    }
    return capability;
  };

  const toggleCostSort = () => {
    setCostSort((current) => {
      if (current === "none") return "asc";
      if (current === "asc") return "desc";
      return "none";
    });
  };

  useEffect(() => {
    setSelectedProvider(defaultProvider || null);
  }, [defaultProvider]);

  return (
    <Modal
      isOpen={isModelInfoOpen}
      onClose={onModelInfoClose}
      size="6xl"
      motionPreset="scale"
      isCentered
    >
      <ModalOverlay bg="blackAlpha.300" backdropFilter="blur(10px)" />
      <ModalContent
        as={motion.div}
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        transition={{ duration: "0.2s" }}
      >
        <ModalHeader>{t("chat.modelInfo.title")}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {isLoading ? (
            <Box textAlign="center" py={4}>
              <Spinner />
            </Box>
          ) : (
            <VStack spacing={4} align="stretch">
              <Box>
                <Text fontSize="sm" mb={2} fontWeight="medium">
                  {t("chat.modelInfo.filterByProvider")}
                </Text>
                <Wrap spacing={2}>
                  <WrapItem>
                    <Button
                      size="sm"
                      variant={selectedProvider === null ? "solid" : "outline"}
                      colorScheme="purple"
                      onClick={() => setSelectedProvider(null)}
                    >
                      {t("chat.modelInfo.all")}
                    </Button>
                  </WrapItem>
                  {providers.map((provider) => (
                    <WrapItem key={provider}>
                      <Button
                        size="sm"
                        variant={
                          selectedProvider === provider ? "solid" : "outline"
                        }
                        colorScheme="purple"
                        onClick={() => setSelectedProvider(provider)}
                      >
                        {provider}
                      </Button>
                    </WrapItem>
                  ))}
                </Wrap>
              </Box>

              <Box>
                <Text fontSize="sm" mb={2} fontWeight="medium">
                  {t("chat.modelInfo.filterByCapability")}
                </Text>
                <Wrap spacing={2}>
                  <WrapItem>
                    <Button
                      size="sm"
                      variant={
                        selectedCapability === null ? "solid" : "outline"
                      }
                      colorScheme="blue"
                      onClick={() => setSelectedCapability(null)}
                    >
                      {t("chat.modelInfo.all")}
                    </Button>
                  </WrapItem>
                  {capabilities.map((capability) => (
                    <WrapItem key={capability}>
                      <Button
                        size="sm"
                        variant={
                          selectedCapability === capability
                            ? "solid"
                            : "outline"
                        }
                        colorScheme="blue"
                        leftIcon={
                          <Icon as={getModelIcon(capability)} boxSize={4} />
                        }
                        onClick={() => setSelectedCapability(capability)}
                      >
                        {getModelCapabilityName(capability)}
                      </Button>
                    </WrapItem>
                  ))}
                </Wrap>
              </Box>

              <HStack>
                <Button
                  size="sm"
                  variant={showDisabled ? "solid" : "outline"}
                  colorScheme="gray"
                  onClick={() => setShowDisabled(!showDisabled)}
                  leftIcon={<Icon as={FiEye} />}
                >
                  {t("chat.modelInfo.showDisabled")}
                </Button>

                <Button
                  size="sm"
                  variant={costSort !== "none" ? "solid" : "outline"}
                  colorScheme="orange"
                  onClick={toggleCostSort}
                  leftIcon={
                    <Icon
                      as={
                        costSort === "none"
                          ? FaSort
                          : costSort === "asc"
                          ? FaSortNumericUp
                          : FaSortNumericDown
                      }
                    />
                  }
                >
                  {costSort === "none"
                    ? t("chat.modelInfo.sortByCost")
                    : costSort === "asc"
                    ? t("chat.modelInfo.costLowToHigh")
                    : t("chat.modelInfo.costHighToLow")}
                </Button>
              </HStack>
              <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
                {filteredModels.map((model) => (
                  <ModelInfoCard
                    key={model.id}
                    model={model}
                    currencyPairs={currencyPairs}
                    getModelIcon={getModelIcon}
                    getModelCapabilityName={getModelCapabilityName}
                  />
                ))}
              </SimpleGrid>
            </VStack>
          )}
        </ModalBody>
        <ModalFooter>
          <Text fontSize="sm" fontStyle="italic" color="gray.500" mr={4}>
            {t("chat.modelInfo.notice")}
          </Text>
          <IconButton
            aria-label={t("chat.modelInfo.close")}
            icon={<FiX />}
            onClick={onModelInfoClose}
            variant="outline"
            colorScheme="green"
            size="sm"
          />
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
